import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import type { FormProps } from '@leafwell/components';
import type { SelectFieldControlledProps } from './select-field';
import type { SearchStrainsQueryVariables } from 'graphql/wp/types-and-hooks';
import type {
  GetInitialStrainsData,
  GetStrainsFilterData,
  GetStrainsFiltersData,
  StrainsFilters,
} from 'lib/queries/strains';
import type { PaginationProps } from './pagination-clientside';

import React, {
  useCallback,
  useRef,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { ApolloProvider, useApolloClient } from '@apollo/client';
import {
  useUniqueId,
  useFormContext,
  useToast,
  Button,
  Form,
  Link,
  Paragraph,
  ToggleFieldControlled,
  Wrap,
} from '@leafwell/components';
import { useSearchStrainsLazyQuery } from 'graphql/wp/types-and-hooks';
import { useMatchMedia } from 'hooks/useMatchMedia';
import { useWordPressContext } from 'contexts/wordpress-provider';
import {
  STRAINS_PER_PAGE,
  getStrainsFilterData,
  getStrainsQueryWhere,
  getThcLevelsRange,
} from 'lib/queries/strains';
import StrainCard from './strain-card';
import { useStrainsArchiveFilters } from 'hooks/useStrainsArchive';
import SelectMultiple, { SelectMultipleGetOptions } from './select-multiple';
import { useRouter } from 'next/router';
import { getLanguageCodeFilter } from 'lib/queries/settings';
import Pagination from './pagination-clientside';
import Loader from './loader';
import { debounce } from 'lib/utils/debounce';
import { useEventsTracker } from '../contexts/eventsTracker';
import { initializeWpApollo } from 'lib/apollo';
import AlphabetList from './alphabet-list';

const DEFAULT_VARIABLES = {
  aromas: [],
  conditionsIds: [],
  firstLetter: '',
  flavors: [],
  positiveEffects: [],
  strainTypes: '',
  terpenes: [],
  thcQuantity: [],
};

const DEFAULT_VALUES = {
  initialData: null,
  loading: false,
  strains: null,
  variables: DEFAULT_VARIABLES,
};

export type ArchiveStrainsState = {
  initialData: GetInitialStrainsData;
  strains: GetInitialStrainsData['strains'];
  variables: StrainsFilters;
};

export type ArchiveReducerAction =
  | {
      type: 'updateVariables';
      value: Partial<ArchiveStrainsState['variables']>;
    }
  | {
      type: 'updateStrains';
      value: ArchiveStrainsState['strains'];
    }
  | { type: 'reset' };

function initState(state: Partial<ArchiveStrainsState>): ArchiveStrainsState {
  return {
    ...DEFAULT_VALUES,
    ...state,
  };
}

function archiveStateReducer(
  state: ArchiveStrainsState,
  action: ArchiveReducerAction,
): ArchiveStrainsState {
  switch (action.type) {
    case 'reset': {
      return initState({
        ...state,
        strains: state.initialData.strains,
        variables: state.initialData?.variables
          ? state.initialData.variables
          : DEFAULT_VARIABLES,
      });
    }
    case 'updateVariables': {
      return {
        ...state,
        variables: { ...state.variables, ...action.value },
      };
    }
    case 'updateStrains': {
      return {
        ...state,
        strains: action.value,
      };
    }
  }
}

const ArchivePagination: React.FC<
  Pick<PaginationProps, 'offset' | 'onPageChange' | 'total'>
> = ({ offset, onPageChange, total }) => {
  return total > STRAINS_PER_PAGE ? (
    <Pagination
      {...{
        className: 'col-span-full justify-center',
        limit: STRAINS_PER_PAGE,
        offset,
        onPageChange,
        total,
        variant: 'minimal',
      }}
    />
  ) : null;
};

const ArchiveSelect: React.FC<
  // @ts-ignore
  Pick<SelectFieldControlledProps, 'label' | 'name' | 'placeholder'> & {
    initialOptions: GetStrainsFilterData;
    limit?: number;
  }
> = ({ label, name, initialOptions, limit = 20, placeholder }) => {
  const client = useApolloClient();
  const { locale } = useRouter();
  const { addToast } = useToast();

  const dataSettings = useMemo(() => {
    switch (name) {
      case 'conditionsIds':
        return {
          key: 'id',
          labelKey: 'title',
        };
      default:
        return {
          key: 'slug',
          labelKey: 'name',
        };
    }
  }, [name]);

  const getOptions: SelectMultipleGetOptions = useCallback(
    async ({ offset, search }) => {
      try {
        const { error, ...data } = await getStrainsFilterData({
          client: client as ApolloClient<NormalizedCacheObject>,
          key: name,
          variables: {
            languageCode: getLanguageCodeFilter(locale),
            afterCursor: offset as string,
            search,
          },
        });

        if (error) {
          throw error;
        }

        if (data?.nodes?.length) {
          return {
            data: data.nodes,
            variables: {
              hasNextPage: data.pageInfo.hasNextPage,
              offset: data.pageInfo.endCursor,
            },
          };
        }

        return { data: [], variables: {} };
      } catch (error) {
        addToast({
          content: error?.message,
          key: 'strains-select-error',
          type: 'negative',
          shouldRemove: true,
        });
      }
    },
    [client, locale, name],
  );

  return (
    // @ts-ignore
    <SelectMultiple
      {...{
        dataSettings: {
          ...dataSettings,
          ...(initialOptions.pageInfo.hasNextPage ? { getOptions } : {}),
          cursorPagination: initialOptions.pageInfo,
        },
        name,
        label,
        initialData: initialOptions.nodes,
        limit,
        placeholder,
      }}
    />
  );
};

export type ArchiveFormContentProps = {
  initialOptions: GetStrainsFiltersData;
  initialValues: StrainsFilters;
  onReset: () => void;
  submitOnChange: boolean;
};

const ArchiveFormContent: React.FC<
  Pick<FormProps, 'onSubmit'> & ArchiveFormContentProps
> = ({ initialOptions, initialValues, onReset, onSubmit, submitOnChange }) => {
  const { translations } = useWordPressContext();
  const {
    formState: { isSubmitting, submitCount },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = useFormContext();

  const { trackEvent } = useEventsTracker();

  useEffect(() => {
    if (!isSubmitting && submitCount <= 0) {
      reset(initialValues);
    }
  }, [initialValues]);

  useEffect(() => {
    let subscription = null;
    const unsubscribe = () => subscription?.unsubscribe();

    if (!submitOnChange) {
      unsubscribe();
    } else {
      subscription = watch((_, { type }) => {
        if (type === 'change') {
          // @ts-ignore
          debounce(handleSubmit(onSubmit)(), 150);
        }
      });
    }

    return () => unsubscribe();
  }, [handleSubmit, submitOnChange, watch]);

  return (
    <>
      <section
        {...{
          className:
            'grid md:grid-cols-3 lg:grid-cols-6 items-end gap-x-3 gap-y-6 max-w-screen',
        }}
      >
        <section
          {...{
            className: 'col-span-full grid gap-2',
          }}
        >
          <Paragraph {...{ size: 'xsmall' }}>
            {translations['A-Z Strains']}
          </Paragraph>
          <AlphabetList
            {...{
              disableUrlChange: true,
              initialLetter: initialValues?.firstLetter,
              onChangeLetter: letter => {
                setValue('firstLetter', letter),
                  trackEvent({
                    event: 'select_content',
                    content_type: letter,
                    item_id: 'alphabet_letter_filter',
                    origin: 'strains_directory',
                  });
              },
            }}
          />
        </section>
        <ArchiveSelect
          {...{
            initialOptions: {
              nodes: initialOptions.thcQuantity.map(slug => ({
                name: translations[slug],
                slug,
              })),
              pageInfo: { hasNextPage: false, endCursor: null },
            },
            name: 'thcQuantity',
            label: translations['THC level'],
            placeholder: translations['Select THC level'],
          }}
        />
        <ArchiveSelect
          {...{
            initialOptions: initialOptions.conditionsIds,
            name: 'conditionsIds',
            label: translations['Helps with'],
            placeholder: translations['Select conditions'],
          }}
        />
        <ArchiveSelect
          {...{
            initialOptions: initialOptions.positiveEffects,
            name: 'positiveEffects',
            label: translations['effects'],
            placeholder: translations['Select effects'],
          }}
        />
        <ArchiveSelect
          {...{
            initialOptions: initialOptions.terpenes,
            name: 'terpenes',
            label: translations['Terpenes'],
            placeholder: translations['Select terpenes'],
          }}
        />
        <ArchiveSelect
          {...{
            initialOptions: initialOptions.aromas,
            name: 'aromas',
            label: translations['Aromas'],
            placeholder: translations['Select aromas'],
          }}
        />
        <ArchiveSelect
          {...{
            initialOptions: initialOptions.flavors,
            name: 'flavors',
            label: translations['Flavors'],
            placeholder: translations['Select flavors'],
          }}
        />
        <ToggleFieldControlled
          {...{
            classNameWrap:
              'items-center grid-cols-2 md:grid-cols-none md:grid-flow-col md:col-span-3 lg:col-span-5 sm:justify-start gap-3',
            className: 'font-sans min-h-0 px-3 py-2',
            classNameLabel: 'col-span-full md:sr-only',
            label: translations['Strain type'],
            labelSize: 'xsmall',
            name: 'strainTypes',
            options: [
              { label: translations['All'], value: '' },
              ...initialOptions.strainTypes.nodes.map(({ name, slug }) => ({
                label: name,
                value: slug,
              })),
            ],
            type: 'radio',
            variant: 'button',
          }}
        />
        <Link
          {...{
            className:
              'justify-self-center lg:justify-self-end md:justify-self-start',
            onClick: () => {
              reset();
              onReset();
            },
            variant: 'underline',
          }}
        >
          {translations['Clear filters']}
        </Link>
      </section>
    </>
  );
};

const ArchiveForm: React.FC<
  Pick<FormProps, 'onSubmit'> & Omit<ArchiveFormContentProps, 'submitOnChange'>
> = ({ initialValues, onReset, onSubmit, ...rest }) => {
  const { translations } = useWordPressContext();
  const sectionId = useUniqueId('dashboard-form');
  const [isMediumScreen] = useMatchMedia(['(min-width: 768px)'], [true]);
  const [isFormOpen, toggleForm] = useState(false);

  const hideForm = useCallback(() => {
    toggleForm(false);

    window.document.body.classList.remove('overflow-hidden');
  }, [toggleForm]);

  const showForm = useCallback(() => {
    toggleForm(true);

    window.document.body.classList.add('overflow-hidden');
  }, [toggleForm]);

  const toggleFormVisibility = useCallback(
    (display: boolean) => {
      toggleForm(display);
    },
    [toggleForm],
  );

  useEffect(() => {
    toggleFormVisibility(isMediumScreen);
  }, [isMediumScreen]);

  return (
    <Wrap {...{ id: 'strains-form', spacer: false, size: 'large' }}>
      <Button
        {...{
          'aria-controls': sectionId,
          'aria-expanded': isFormOpen,
          className: 'w-full block md:hidden',
          label: translations['Filter strains'],
          onClick: showForm,
          rounded: true,
          size: 'small',
          variant: 'secondary',
        }}
      />
      <Form
        {...{
          'aria-hidden': !isFormOpen,
          className: [
            'content-start bg-white fixed top-0 left-0 z-40 h-full w-full transition-all overflow-y-auto md:z-10 md:overflow-y-visible md:static',
            isFormOpen ? '' : 'invisible opacity-0',
          ].join(' '),
          defaultValues: useMemo(() => initialValues, [initialValues]),
          id: sectionId,
          onSubmit,
        }}
      >
        <div className="p-5 sm:py-10 grid gap-3 content-start md:p-0">
          <Link
            {...{
              'aria-controls': sectionId,
              'aria-expanded': isFormOpen,
              className: 'block md:hidden justify-self-end',
              onClick: hideForm,
              variant: 'underline',
            }}
          >
            {translations['Close']}
          </Link>
          <ArchiveFormContent
            {...{
              initialValues,
              onReset,
              onSubmit,
              submitOnChange: isMediumScreen,
              ...rest,
            }}
          />
          <Button
            {...{
              className: 'md:hidden',
              label: translations['Filter results'],
              onClick: hideForm,
              rounded: true,
              size: 'small',
              type: 'submit',
              variant: 'dark',
            }}
          />
        </div>
      </Form>
    </Wrap>
  );
};

export type ArchiveStrainsProps = {
  initialData: GetInitialStrainsData;
};

const ArchiveStrains: React.FC<ArchiveStrainsProps> = ({ initialData }) => {
  const { locale } = useRouter();
  const urlFilters = useStrainsArchiveFilters();
  const { translations } = useWordPressContext();
  const { addToast } = useToast();
  const [{ strains, variables }, dispatch] = useReducer(
    archiveStateReducer,
    {
      initialData,
      strains: initialData.strains,
      variables: { ...DEFAULT_VARIABLES, ...initialData.variables },
    },
    initState,
  );

  const [getStrains, { data: responseData, loading, variables: newVariables }] =
    useSearchStrainsLazyQuery({
      onError: error => {
        addToast({
          content:
            error?.message ||
            translations['An error occurred, please try again.'],
          key: 'strains-archive-error',
          type: 'negative',
          shouldRemove: true,
        });
      },
    });

  const getVariables = useCallback(
    ({
      offset = 0,
      thcQuantity,
      ...variables
    }: StrainsFilters & { offset?: number }): SearchStrainsQueryVariables => {
      // cleanup empty values
      Object.entries(variables).forEach(
        ([key, val]) =>
          (val === null || val?.length <= 0) && delete variables[key],
      );

      return {
        where: getStrainsQueryWhere({
          ...variables,
          ...(thcQuantity?.length
            ? { thcQuantity: getThcLevelsRange(thcQuantity) }
            : {}),
          languageCode: getLanguageCodeFilter(locale),
          offset,
        }),
      };
    },
    [locale],
  );

  const onPageChange = useCallback(
    (offset: number) => {
      getStrains({ variables: getVariables({ ...variables, offset }) });
    },
    [getStrains, getVariables, variables],
  );

  const onReset = useCallback(() => {
    dispatch({
      type: 'reset',
    });
  }, [dispatch]);

  const onSubmit = useCallback(
    values => {
      dispatch({ type: 'updateVariables', value: values });
      getStrains({ variables: getVariables(values) });
    },
    [dispatch, getStrains, getVariables],
  );

  const paginationProps = useMemo(
    () => ({
      offset: newVariables?.where?.offsetPagination?.offset,
      onPageChange,
      total: strains?.pageInfo?.offsetPagination?.total,
    }),
    [newVariables, onPageChange, strains],
  );

  useMemo(() => {
    const data = responseData?.strains;

    if (data) {
      dispatch({ type: 'updateStrains', value: data });
    }
  }, [responseData]);

  // if there are query params update data
  // example url ?thcQuantity=["low","high"]&strainTypes=indica&terpenes=["humulene"]
  useEffect(() => {
    if (Object.values(urlFilters).length) {
      dispatch({ type: 'updateVariables', value: urlFilters });
      getStrains({ variables: getVariables(urlFilters) });
    }
  }, [urlFilters]);

  return (
    <>
      <ArchiveForm
        {...{
          initialValues: variables,
          initialOptions: initialData,
          onReset,
          onSubmit,
        }}
      />
      <section
        {...{
          'aria-live': 'polite',
          className: 'bg-gray-100 mt-10 md:mt-12',
          id: 'strains-list',
        }}
      >
        <Wrap
          {...{
            className:
              'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 lg:gap-8',
            size: 'large',
          }}
        >
          <ArchivePagination {...paginationProps} />
          {loading ? (
            <Loader {...{ className: 'col-span-full', fullHeight: false }} />
          ) : (
            <>
              {strains?.nodes?.length > 0 ? (
                strains.nodes.map(strain => (
                  <StrainCard {...{ ...strain, key: strain.id }} />
                ))
              ) : (
                <Paragraph> {translations['No results were found']}</Paragraph>
              )}
            </>
          )}

          <ArchivePagination {...paginationProps} />
        </Wrap>
      </section>
    </>
  );
};

export default function (props) {
  const apolloClient = initializeWpApollo();

  return (
    <ApolloProvider client={apolloClient}>
      <ArchiveStrains {...props} />;
    </ApolloProvider>
  );
}
